import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Service } from '../types';

const ServicesSection = () => {
  const services: Service[] = [
    {
      id: "1",
      title: "AI Customer Support Agents", // SEO: "Agents" ranks better than just "Automation"
      description: "Deploy 24/7 intelligent chatbots that resolve 80% of support tickets instantly without human intervention.",
      icon: "Bot",
      features: ["Auto-Ticket Resolution", "Human Handoff", "Multi-Language", "CRM Sync"],
      link: "/ai-chatbot-automation"
    },
    {
      id: "2",
      title: "Enterprise Workflow Ops",
      description: "Connect your disjointed stack (HubSpot, Shopify, Slack) into a unified machine using n8n and Make.com.",
      icon: "Workflow", // Ensure you have a 'Workflow' or 'GitBranch' icon
      features: ["Cross-App Syncing", "Error Handling", "Data Transformation", "Automated Reporting"],
      link: "/n8n-make-com-workflows"
    },
    {
      id: "3",
      title: "WhatsApp Growth Engines",
      description: "Turn WhatsApp into a revenue channel. Automate abandoned carts, appointment reminders, and lead nurturing.",
      icon: "MessageCircle",
      features: ["98% Open Rates", "Auto-Follow ups", "Broadcast Marketing", "Interactive Menus"],
      link: "/whats-app-email-automation"
    },
    {
      id: "4",
      title: "Custom API Development",
      description: "Need something unique? We build custom endpoints and Python scripts to bridge gaps that standard tools can't.",
      icon: "Code", // 'Code' or 'Terminal' icon
      features: ["Python Scripting", "Secure Webhooks", "Legacy System Connect", "Scalable Architecture"],
      link: "/custom-api-integrations"
    }
  ];

  const digitalMarketingServices: Service[] = [
    {
      id: "5",
      title: "SEO Services",
      description: "Rank higher on Google and turn organic search into your most reliable source of new business.",
      icon: "Search",
      features: ['Technical SEO Audits', 'Keyword Strategy', 'On-Page Optimization', 'Link Building'],
      link: "/seo-services"
    },
    {
      id: "6",
      title: "Local SEO",
      description: "Own the map pack and 'near me' searches so nearby customers find you first.",
      icon: "MapPin",
      features: ['Google Business Profile', 'Local Citations', 'Review Management', 'Local Landing Pages'],
      link: "/local-seo"
    },
    {
      id: "7",
      title: "Social Media Marketing",
      description: "Content, community, and campaigns that turn followers into enquiries and customers.",
      icon: "Share2",
      features: ['Content Calendars', 'Reels & Creatives', 'Community Management', 'Paid Social Boosts'],
      link: "/social-media-marketing"
    },
    {
      id: "8",
      title: "Google Ads & PPC",
      description: "Search and shopping ads that put you at the top of Google from day one, with every rupee tracked.",
      icon: "MousePointerClick",
      features: ['Search Campaigns', 'Shopping & Performance Max', 'Landing Page CRO', 'Conversion Tracking'],
      link: "/google-ads-ppc"
    },
    {
      id: "9",
      title: "Meta Ads",
      description: "Facebook and Instagram ads engineered for leads, sales, and retargeting, not just likes.",
      icon: "Megaphone",
      features: ['Audience Targeting', 'Creative Testing', 'Retargeting Funnels', 'Lead Forms & CAPI'],
      link: "/meta-ads"
    },
    {
      id: "10",
      title: "Content Marketing",
      description: "Blogs, videos, and guides that answer buyer questions, build trust, and keep ranking for years.",
      icon: "PenLine",
      features: ['SEO Blog Writing', 'Video Scripts', 'Lead Magnets', 'Content Calendars'],
      link: "/content-marketing"
    },
    {
      id: "11",
      title: "Email Marketing",
      description: "Campaigns and automations that turn your list into repeat revenue, without spamming anyone.",
      icon: "Mail",
      features: ['Newsletter Campaigns', 'Drip Sequences', 'List Segmentation', 'Template Design'],
      link: "/email-marketing"
    },
    {
      id: "12",
      title: "Analytics & CRO",
      description: "Dashboards, experiments, and fixes that turn more of your existing traffic into customers.",
      icon: "BarChart3",
      features: ['GA4 & Tag Setup', 'Conversion Audits', 'A/B Testing', 'Funnel Reports'],
      link: "/analytics-and-cro"
    },
  ];

  return (
    <section className="bg-background py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-flex items-center space-x-2 px-3 py-1 bg-accent/10 border border-accent/20 rounded-full">
            <Icon name="Cpu" size={14} className="text-accent" />
            <span className="text-xs font-semibold text-accent uppercase tracking-wider">End-to-End Solutions</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary tracking-tight">
            Comprehensive <span className="text-primary">Automation Services</span>
          </h2>
          
          <p className="text-lg text-text-secondary">
            We don't just write code; we re-engineer your entire business logic to be faster, cheaper, and more reliable.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-10 mb-16">
          {services.map((service) => (
            <div
              key={service.id}
              className="group relative bg-card rounded-2xl p-8 border border-border shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              {/* Hover Gradient Effect */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-accent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-colors duration-300">
                  <Icon name={service.icon} size={28} className="text-primary group-hover:text-white transition-colors" />
                </div>
                
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-text-primary mb-2">
                      {service.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed">
                      {service.description}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                        <span className="text-sm font-medium text-text-secondary">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4">
                    <Link to={service.link} className="inline-flex items-center text-primary font-semibold hover:text-accent transition-colors group-hover:translate-x-1 duration-300">
                      Explore Solution 
                      <Icon name="ArrowRight" size={16} className="ml-2" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>


        {/* Digital Marketing Services */}
        <div className="text-center max-w-3xl mx-auto mb-12 mt-20 space-y-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary tracking-tight">
            Digital <span className="text-primary">Marketing Services</span>
          </h2>
          <p className="text-lg text-text-secondary">
            Full-funnel marketing that brings in leads while automation converts them.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-10 mb-16">
          {digitalMarketingServices.map((service) => (
            <div
              key={service.id}
              className="group relative bg-card rounded-2xl p-8 border border-border shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-accent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              <div className="flex items-start gap-6">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-colors duration-300">
                  <Icon name={service.icon} size={28} className="text-primary group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-text-primary mb-2">
                      {service.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                        <span className="text-sm font-medium text-text-secondary">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-4">
                    <Link to={service.link} className="inline-flex items-center text-primary font-semibold hover:text-accent transition-colors group-hover:translate-x-1 duration-300">
                      Explore Solution 
                      <Icon name="ArrowRight" size={16} className="ml-2" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* View All Button */}
        <div className="text-center">
          <Link to="/services-overview">
            <Button variant="outline" size="lg" className="border-2 hover:bg-primary/5">
              View All 15+ Integration Types
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;